"use client";

import { useEffect, useState } from "react";

import cn from "classnames";
import type { Swiper as SwiperType } from "swiper";

import { ArrowLeftIcon } from "@/components/icons/arrowLeft.icon";
import { ArrowRightIcon } from "@/components/icons/arrowRight.icon";

const SWIPER_EVENTS = [
  "init",
  "slideChange",
  "snapGridLengthChange",
  "breakpoint",
  "resize",
  "progress",
] as const;

type ControlsState = {
  pageCount: number;
  activePage: number;
  isBeginning: boolean;
  isEnd: boolean;
};

// The last page is clamped to the end of the track, and `snapIndex` can lag
// behind it mid-transition, so the edges decide the first and last dot.
const readState = (swiper: SwiperType): ControlsState => {
  const pageCount = swiper.snapGrid.length;
  const activePage = swiper.isEnd
    ? pageCount - 1
    : swiper.isBeginning
      ? 0
      : swiper.snapIndex;
  return {
    pageCount,
    activePage,
    isBeginning: swiper.isBeginning,
    isEnd: swiper.isEnd,
  };
};

const sameState = (a: ControlsState | null, b: ControlsState) =>
  a?.pageCount === b.pageCount &&
  a.activePage === b.activePage &&
  a.isBeginning === b.isBeginning &&
  a.isEnd === b.isEnd;

/**
 * Page dots plus prev/next arrows for a Swiper. One dot per snap (page), and
 * arrows move one `slidesPerGroup` at a time. Below `lg` the arrows sit on
 * either side of the dots; from `lg` the dots are centered and the arrows are
 * grouped at the right. Renders nothing when everything fits on one page.
 */
export function CarouselControls({
  swiper,
  ariaLabel,
  className,
}: CarouselControlsProps) {
  const [state, setState] = useState<ControlsState | null>(null);

  useEffect(() => {
    if (!swiper || swiper.destroyed) return;
    // `progress` fires on every drag frame; skip renders when nothing changed.
    const update = () => {
      const next = readState(swiper);
      setState((prev) => (sameState(prev, next) ? prev : next));
    };
    update();
    SWIPER_EVENTS.forEach((event) => swiper.on(event, update));
    return () => {
      if (swiper.destroyed) return;
      SWIPER_EVENTS.forEach((event) => swiper.off(event, update));
    };
  }, [swiper]);

  if (!swiper || !state || state.pageCount <= 1) return null;

  const goToPage = (page: number) =>
    swiper.slideTo(page * (Number(swiper.params.slidesPerGroup) || 1));

  const prev = (
    <ArrowButton
      direction="prev"
      disabled={state.isBeginning}
      onClick={() => swiper.slidePrev()}
    />
  );
  const next = (
    <ArrowButton
      direction="next"
      disabled={state.isEnd}
      onClick={() => swiper.slideNext()}
    />
  );
  const dots = (
    <div
      role="group"
      aria-label={ariaLabel}
      className="flex items-center justify-center gap-2">
      {Array.from({ length: state.pageCount }, (_, page) => {
        const isActive = page === state.activePage;
        return (
          <button
            key={page}
            type="button"
            aria-label={`Go to page ${page + 1} of ${state.pageCount}`}
            aria-current={isActive ? "true" : undefined}
            className={cn(
              "focus-visible:ring-secondary size-2 rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
              isActive ? "bg-secondary" : "bg-greyscale-strength/40",
            )}
            onClick={() => goToPage(page)}
          />
        );
      })}
    </div>
  );

  // Two layouts rather than reordering one, so focus order matches what is
  // on screen at each breakpoint. The hidden copy is display:none.
  return (
    <div data-testid="carousel-controls" className={cn("w-full", className)}>
      <div className="flex items-center justify-center gap-6 lg:hidden">
        {prev}
        {dots}
        {next}
      </div>
      <div className="hidden grid-cols-3 items-center lg:grid">
        <div />
        {dots}
        <div className="flex justify-end gap-4">
          {prev}
          {next}
        </div>
      </div>
    </div>
  );
}

/** Same look as `CarouselCenteredNavButton`, plus a disabled state at the ends of the track. */
function ArrowButton({ direction, disabled, onClick }: ArrowButtonProps) {
  const isPrev = direction === "prev";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-disabled={disabled}
      aria-label={isPrev ? "Previous page" : "Next page"}
      className="border-secondary text-secondary focus-visible:ring-secondary flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full border transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-40 lg:min-h-10 lg:min-w-10">
      {isPrev ? (
        <ArrowLeftIcon width={24} height={24} aria-hidden />
      ) : (
        <ArrowRightIcon width={24} height={24} aria-hidden />
      )}
    </button>
  );
}

type ArrowButtonProps = {
  direction: "prev" | "next";
  disabled: boolean;
  onClick: () => void;
};

export type CarouselControlsProps = {
  /** The Swiper instance, e.g. from `onSwiper={setSwiper}`. */
  swiper: SwiperType | null;
  /** Accessible name for the page dots, e.g. "Card pages". */
  ariaLabel: string;
  className?: string;
};
